import {
    AfterViewInit,
    ChangeDetectorRef,
    Component,
    OnDestroy,
    OnInit,
    ViewChild,
    ViewEncapsulation,
} from '@angular/core';
import { WellHealthinessService } from 'app/modules/well-healthiness/services/well-healthiness.service';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { EMPTY, merge, Subject, switchMap, takeUntil } from 'rxjs';
import module from 'app/modules/well-healthiness/well-healthiness.module';

@Component({
    selector: 'app-well-healthiness-string-summary',
    templateUrl: './string-summary.page.html',
    standalone: true,
    imports: [module, MatPaginatorModule, MatSortModule, MatProgressSpinnerModule],
    styleUrls: ['./string-summary.page.scss'],
    host: {
        class: 'block w-full h-full bg-white flex flex-col min-w-0 flex-auto',
    },
})
export class StringSummaryPage implements OnInit, AfterViewInit, OnDestroy {
    @ViewChild(MatPaginator) private readonly _paginator: MatPaginator;
    @ViewChild(MatSort) private readonly _sort: MatSort;
    private readonly _unsubscribeAll: Subject<any> = new Subject<any>();

    searchForm: UntypedFormGroup;
    dt: any;
    dtConfig: any;
    startingIndex: number = 0;
    isLoading: boolean = false;

    constructor(
        public service: WellHealthinessService,
        private readonly _formBuilder: UntypedFormBuilder,
        private readonly router: Router,
        private readonly route: ActivatedRoute,
        private readonly _changeDetectorRef: ChangeDetectorRef,
    ) {}

    ngOnInit() {
        this.setSearch();
        this.dtConfig = this.setDataTable();

        this.route.queryParamMap
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((queryParams) => {
                const selectedStatus =
                    queryParams.get('WellHealthinessStatus') ?? null;
                const riskRanking = queryParams.get('RiskRanking') ?? null;

                this.searchForm.patchValue(
                    {
                        WellHealthinessStatus: selectedStatus,
                        RiskRanking: riskRanking,
                        Page: 1,
                    },
                    { emitEvent: false },
                );

                if (this.dt?.page) {
                    this.dt.page.offset = 0;
                }

                this.fetchStringSummaryData();
            });
    }

    fetchStringSummaryData() {
        const params = this.searchForm.getRawValue();
        this.service
            .getWellHealthinessStringsList(params)
            .subscribe((response) => {
                if (response?.data?.data) {
                    const transformedData = response.data.data.map((item) => ({
                        fieldLongName: item.fieldLongName,
                        platformLongName: item.platformLongName,
                        wellLongName: item.wellLongName,
                        stringShortName: item.stringShortName,
                        stringLongName: item.stringLongName,
                        stringIndicatorShortName: item.stringIndicatorShortName,
                        result: item.inspectionResult,
                        tester: item.tester,
                        status: item.status,
                        wellStringId: item.wellStringId,
                        riskRanking: item.riskRanking,
                    }));

                    this.dt.page.totalPages = response.data.totalPages;
                    this.dt.page.totalRecords = response.data.totalRecords;
                    this.dt.page.size = params.PageSize;
                    this.dt.rows = transformedData.map((row, index) => ({
                        ...row,
                        no: this.startingIndex + index + 1,
                    }));

                    this._changeDetectorRef.markForCheck();
                }
            });
    }

    ngAfterViewInit() {
        if (this._sort && this._paginator) {
            // Set the initial sort defaults in paginator/sort if searchForm has values
            
            // If the user changes the sort order, reset back to the first page.
            this._sort.sortChange
                .pipe(takeUntil(this._unsubscribeAll))
                .subscribe(() => {
                    this._paginator.pageIndex = 0;
                });

            // If sort or page changes, fetch data
            merge(this._sort.sortChange, this._paginator.page)
                .pipe(takeUntil(this._unsubscribeAll))
                .subscribe(() => {
                    const pageIndex = this._paginator.pageIndex + 1;
                    const pageSize = this._paginator.pageSize || this.searchForm.get('PageSize')?.value || 10;
                    
                    this.searchForm.patchValue({ 
                        Page: pageIndex,
                        PageSize: pageSize,
                        OrderBy: this._sort.active || null,
                        OrderDir: this._sort.direction || null
                    }, { emitEvent: false });
                    this.fetchStringSummaryData();
                });
        }
    }

    ngOnDestroy() {
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    setSearch() {
        this.searchForm = this._formBuilder.group({
            Page: [1],
            PageSize: [10],
            OrderBy: [null],
            OrderDir: [null],
            WellHealthinessStatus: [null],
            RiskRanking: [null],
        });

        this.searchForm
            .get('PageSize')
            .valueChanges.pipe(takeUntil(this._unsubscribeAll))
            .subscribe((value) => {
                this.dt.page.offset = 0;
                this.searchForm.patchValue({ Page: 1 }, { emitEvent: false });
                this.fetchStringSummaryData();
            });
    }

    setSize(size: number) {
        this.dt.page.size = size;
        this.dt.page.offset = 0;
        this.searchForm.patchValue(
            { Page: 1, PageSize: size },
            { emitEvent: false },
        );
        this.fetchStringSummaryData();
    }

    setDataTable() {
        this.dt = {
            page: {
                size: this.searchForm.get('PageSize')?.value,
                totalRecords: 0,
                totalPages: 0,
                offset: 0,
            },
            rows: [],
        };

        return {
            displayedColumns: [
                'no', 'fieldLongName', 'platformLongName', 'wellLongName', 
                'stringShortName', 'status', 'result', 'riskRanking'
            ],
            column: {
                header: [
                    {
                        key: 'no',
                        name: 'No',
                        props: { sortable: false },
                    },
                    {
                        key: 'fieldLongName',
                        name: 'Field',
                        props: { sortable: true },
                    },
                    {
                        key: 'platformLongName',
                        name: 'Platform',
                        props: { sortable: true },
                    },
                    {
                        key: 'wellLongName',
                        name: 'Well',
                        props: { sortable: true },
                    },
                    {
                        key: 'stringShortName',
                        name: 'Well String',
                        props: { sortable: true },
                    },
                    {
                        key: 'status',
                        name: 'Status',
                        props: { sortable: true },
                    },
                    {
                        key: 'result',
                        name: 'Well Healthiness Status',
                        props: { sortable: true },
                    },
                    {
                        key: 'riskRanking',
                        name: 'Risk Ranking',
                        props: { sortable: true },
                    },
                ],
            },
        };
    }

    toFieldSummary() {
        this.router.navigate([`/wim/well-healthiness/dashboard/field`]);
    }

    reset() {
        const hasStatusFilter = this.route.snapshot.queryParamMap.has(
            'WellHealthinessStatus',
        );
        const hasRiskRankingFilter = this.route.snapshot.queryParamMap.has(
            'RiskRanking',
        );

        this.router.navigate([], {
            relativeTo: this.route,
            queryParams: { WellHealthinessStatus: null, RiskRanking: null },
            queryParamsHandling: 'merge',
        });

        this.searchForm.patchValue(
            {
                Page: 1,
                PageSize: 10,
                OrderBy: null,
                OrderDir: null,
                WellHealthinessStatus: null,
                RiskRanking: null,
            },
            { emitEvent: false },
        );

        this.dt.page.offset = 0;

        // Query param changes trigger fetch through the existing subscription.
        if (!hasStatusFilter && !hasRiskRankingFilter) {
            this.fetchStringSummaryData();
        }
    }

    onPage(pageInfo: any) {
        // Obsolete with MatPaginator
    }

    onSort(sortEvent: any) {
        // Obsolete with MatSort
    }

    trackByFn(index: number): number {
        return index;
    }

    downloadStringsListExcel() {
        const formValues = this.searchForm.getRawValue();
        const exportParams: any = {};
        
        if (formValues.WellHealthinessStatus) {
            exportParams.WellHealthinessStatus = formValues.WellHealthinessStatus;
        }
        if (formValues.RiskRanking) {
            exportParams.RiskRanking = formValues.RiskRanking;
        }

        this.service.getWellHealthinessStringsListExport(exportParams).subscribe((blob) => {
            const { date, time } = this.getFormattedDateTime();
            const url = globalThis.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `Well_Healthiness_Strings_List_${date}_${time}.xlsx`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            globalThis.URL.revokeObjectURL(url);
        });
    }

    private getFormattedDateTime() {
        const now = new Date();
        const date = now.toISOString().split('T')[0]; // YYYY-MM-DD
        const time = now.toTimeString().split(' ')[0]; // HH:MM:SS
        return { date, time };
    }
}
